/**
 * CADModelList - List of uploaded CAD models
 */

import React from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import {
  ViewInAr as ModelIcon,
  Delete as DeleteIcon,
  CheckCircle as CompletedIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Compare as CompareIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CADModel } from '../../types/cad';

interface CADModelListProps {
  models: CADModel[];
  selectedModelId: string | null;
  onSelect: (model: CADModel) => void;
  onDelete: (modelId: string) => void;
  onSetComparisonTarget?: (model: CADModel) => void;
  comparisonModelId?: string | null;
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'completed':
      return <CompletedIcon fontSize="small" color="success" />;
    case 'failed':
      return <ErrorIcon fontSize="small" color="error" />;
    default:
      return <PendingIcon fontSize="small" color="disabled" />;
  }
}

function formatDate(isoString: string, language: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString(language.startsWith('en') ? 'en-US' : 'ja-JP', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const CADModelList: React.FC<CADModelListProps> = ({
  models,
  selectedModelId,
  onSelect,
  onDelete,
  onSetComparisonTarget,
  comparisonModelId,
}) => {
  const { i18n, t } = useTranslation();

  if (models.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="body2">
          {t('cad.modelList.emptyTitle')}
        </Typography>
        <Typography color="text.disabled" variant="caption">
          {t('cad.modelList.emptyDescription')}
        </Typography>
      </Box>
    );
  }

  return (
    <List dense disablePadding>
      {models.map((model) => (
        <ListItemButton
          key={model.id}
          selected={model.id === selectedModelId}
          onClick={() => onSelect(model)}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <ModelIcon fontSize="small" color={model.id === selectedModelId ? 'primary' : 'action'} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                {model.filename}
              </Typography>
            }
            secondary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                {getStatusIcon(model.status)}
                <Typography variant="caption" color="text.disabled">
                  {formatDate(model.createdAt, i18n.language)}
                </Typography>
              </Box>
            }
          />
          <ListItemSecondaryAction>
            {onSetComparisonTarget && model.id !== selectedModelId && model.status === 'completed' && (
              <Tooltip title={t('cad.modelList.setComparisonTarget')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetComparisonTarget(model);
                  }}
                  color={model.id === comparisonModelId ? 'primary' : 'default'}
                >
                  <CompareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('cad.modelList.delete')}>
              <IconButton
                edge="end"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(model.id);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ListItemSecondaryAction>
        </ListItemButton>
      ))}
    </List>
  );
};

export default CADModelList;
